/* How this score was built: one row per dimension, the sentence that names the culprit, and the
 * four identifiers that make the number reproducible.
 *
 * The question a data steward is actually asked is *why is this number what it is?* — and a total
 * nobody can take apart is the artefact `docs/design/SCORING.md` calls indefensible. So the
 * decomposition is the drawer's first tab rather than something behind a disclosure.
 *
 * **A dimension with no value says which of four reasons applies.** "You have not written rules for
 * this" and "Lumiq cannot measure this" are different facts and only one is the customer's to fix.
 * `accuracy` is the standing example: no rule kind can express "matches reality", so it is empty on
 * every score in every tenant, and a screen that rendered that as a coverage gap would send
 * somebody to write rules that cannot exist.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';

import type { Score } from '../api/scoring';
import { absence } from '../model/dimensions';
import {
  diagnose,
  formatRatio,
  formatScore,
  orderedDimensions,
  scoreBand,
  worstDimension,
} from '../model/presentation';
import type { DrawerTab } from '../model/types';

export interface ScoreDecompositionProps {
  readonly score: Score;
  readonly onGoTo: (tab: DrawerTab) => void;
}

export function ScoreDecomposition({
  score,
  onGoTo,
}: ScoreDecompositionProps): ReactElement {
  const { t } = useTranslation();
  const worst = worstDimension(score);
  const finding = diagnose(score);
  const totalLost = score.contributions.reduce(
    (sum, entry) => sum + entry.points_lost,
    0,
  );

  const coverage = formatRatio(score.coverage.column_ratio);
  const withoutEvidence = score.dimensions_without_evidence.filter(
    (dimension) => !score.unmeasurable_dimensions.includes(dimension),
  );

  const qualifiers: string[] = [];
  if (!score.evidence_complete) qualifiers.push(t('scoring.qualifier.partial'));
  if (coverage !== null && coverage < 80)
    qualifiers.push(t('scoring.qualifier.coverage', { percent: coverage }));
  if (withoutEvidence.length > 0) {
    qualifiers.push(t('scoring.qualifier.noEvidence', { count: withoutEvidence.length }));
  }

  return (
    <div className="lq-stack">
      {qualifiers.length > 0 && (
        <Alert tone="warning" title={t('scoring.score.qualified')}>
          <p className="lq-nomargin">
            {t('scoring.qualifier.lede')} {qualifiers.join(t('scoring.qualifier.join'))}.
          </p>
        </Alert>
      )}

      <section>
        <h3 className="lq-drawer__h">{t('scoring.decomposition.dimension')}</h3>
        <div className="lq-decomp">
          {orderedDimensions(score).map((entry) => {
            const isWorst = entry.dimension === worst;
            const band = entry.value === null ? null : scoreBand(entry.value);
            const lostHere = score.contributions
              .filter((contribution) => contribution.dimension === entry.dimension)
              .reduce((sum, contribution) => sum + contribution.points_lost, 0);
            const records = score.contributions
              .filter((contribution) => contribution.dimension === entry.dimension)
              .reduce((sum, contribution) => sum + contribution.violation_count, 0);

            return (
              <div className="lq-decomp__row" key={entry.dimension}>
                <span className="lq-decomp__label">
                  {t(`scoring.dimension.${entry.dimension}`)}
                  {isWorst && (
                    <span
                      className="lq-decomp__worstmark"
                      title={t('scoring.decomposition.worstHint')}
                    >
                      {t('scoring.decomposition.worst')}
                    </span>
                  )}
                </span>

                {/* The hatched track for an unmeasured dimension, never an empty one: a
                    zero-width fill and a measured zero are the same picture. */}
                <span
                  className={
                    entry.value === null
                      ? 'lq-scores__track lq-scores__track--absent'
                      : 'lq-scores__track'
                  }
                >
                  {entry.value !== null && (
                    <span
                      className={`lq-scores__fill lq-scores__fill--${band === 'ok' ? 'ok' : band === 'warn' ? 'warn' : 'danger'}`}
                      style={{
                        width: `${String(Math.max(0, Math.min(100, entry.value)))}%`,
                      }}
                    />
                  )}
                </span>

                {entry.value === null ? (
                  <span className="lq-decomp__figure lq-decomp__figure--absent">
                    {t(absence(entry, score))}
                  </span>
                ) : (
                  <>
                    <span
                      className={
                        isWorst
                          ? 'lq-decomp__figure lq-decomp__figure--worst'
                          : 'lq-decomp__figure'
                      }
                    >
                      {formatScore(entry.value)}
                    </span>
                    <span className="lq-decomp__meta">
                      {t('scoring.decomposition.weightAndLoss', {
                        weight: entry.weight,
                        lost: lostHere.toFixed(1),
                      })}
                      <span className="lq-decomp__records">
                        {t('scoring.decomposition.records', {
                          checks: score.contributions.filter(
                            (contribution) =>
                              contribution.dimension === entry.dimension &&
                              contribution.violation_count > 0,
                          ).length,
                          counted: entry.rules_counted,
                          records,
                        })}
                      </span>
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="lq-drawer__h">{t('scoring.decomposition.diagnosisHead')}</h3>
        {finding === null ? (
          <p className="lq-muted lq-nomargin">
            {totalLost <= 0
              ? t('scoring.decomposition.nothingLost')
              : t('scoring.decomposition.noneGuidance')}
          </p>
        ) : (
          <>
            <p className="lq-decomp__diagnosis">
              {t('scoring.decomposition.diagnosis', {
                dimension: t(`scoring.dimension.${finding.dimension}`),
                share: finding.share,
                rule: finding.ruleName,
                violations: finding.violationCount,
                rows: finding.rowsEvaluated,
              })}
            </p>
            <div className="lq-drawer__actions">
              <button
                type="button"
                className="lq-btn lq-btn--secondary lq-btn--compact"
                onClick={() => {
                  onGoTo('checks');
                }}
              >
                {t('scoring.action.inspectChecks')}
              </button>
              <button
                type="button"
                className="lq-btn lq-btn--secondary lq-btn--compact"
                onClick={() => {
                  onGoTo('change');
                }}
              >
                {t('scoring.action.whyChanged')}
              </button>
            </div>
          </>
        )}
      </section>

      {score.unattributed_rules > 0 && (
        <p
          className="lq-muted lq-nomargin"
          title={t('scoring.score.unattributedMeaning')}
        >
          {t('scoring.score.unattributed', { count: score.unattributed_rules })}
        </p>
      )}

      <section>
        <h3 className="lq-drawer__h">{t('scoring.provenance.head')}</h3>
        <p className="lq-muted lq-nomargin">{t('scoring.provenance.lede')}</p>
        <table className="lq-table lq-table--compact lq-provenance">
          <caption className="lq-visually-hidden">
            {t('scoring.provenance.caption')}
          </caption>
          <tbody>
            <tr>
              <th scope="row">{t('scoring.provenance.datasetVersion')}</th>
              <td className="lq-mono">{score.dataset_version_id}</td>
            </tr>
            <tr>
              <th scope="row">{t('scoring.provenance.execution')}</th>
              <td className="lq-mono">{score.execution_id}</td>
            </tr>
            <tr>
              <th scope="row">{t('scoring.provenance.planHash')}</th>
              <td className="lq-mono" title={t('scoring.provenance.planHashHint')}>
                {score.plan_hash}
              </td>
            </tr>
            <tr>
              <th scope="row">{t('scoring.provenance.model')}</th>
              <td className="lq-mono">
                {t('scoring.model.version', { version: score.score_model_version })}
              </td>
            </tr>
            <tr>
              <th scope="row">{t('scoring.provenance.coverage')}</th>
              <td>
                {t('scoring.score.columns', {
                  covered: score.coverage.columns_with_a_rule,
                  total: score.coverage.columns_total,
                })}
                {' · '}
                {t('scoring.score.dimensions', {
                  scored: score.coverage.dimensions_scored,
                  weighted: score.coverage.dimensions_weighted,
                })}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}
